import apiEndpoint from '../../../config/data';
import { showToast } from '@spotoninc/nexus-react';
import has from 'lodash/has';
import size from 'lodash/size';
import { mergeUsersToTasks } from '../tasks.utils';
import { getAuthHeader } from '../../../../utils/tokenService';
import { trackTaskDeleted } from '../../../../utils/analytics/taskTracking';

/**
 * Task Manager Service
 * Contains all API calls related to task management
 */
class TaskManagerService {
  /**
   * Get authorization headers for API calls
   * @returns {Object} Headers object with authorization
   */
  getAuthHeaders() {
    const authHeader = getAuthHeader();
    return authHeader ? { Authorization: authHeader } : {};
  }

  /**
   * Fetch all users from the API
   * @returns {Promise<Array>} Promise that resolves to array of users
   */
  async fetchUsers() {
    try {
      const response = await fetch(apiEndpoint + '/api/get_users/', {
        method: 'GET',
        credentials: 'include',
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        // Throw an error to be caught by the catch block
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      return Promise.resolve(data.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  async fetchLocations() {
    try {
      const response = await fetch(apiEndpoint + '/api/fetch_locations/', {
        method: 'POST',
        credentials: 'include',
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        // Throw an error to be caught by the catch block
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      let modifiedData = [];
      if (data && data.length > 0) {
        modifiedData = data.map(item => ({
          label: item.ui_label,
          value: item.ui_label,
          currency: item.currency,
          ddl_value: item.ddl_value,
          period_length: item.period_length,
          // Capture `location_id_id` as returned by the backend (e.g., Django FK field)
          // and normalize it to `locationId` for use in filtering on the client.
          locationId: item.location_id_id,
          // Extract integration id from ddl_value (format: <...>|<integrationId>|...)
          integrationId:
            typeof item.ddl_value === 'string'
              ? item.ddl_value.split('|')[1]
              : null,
        }));
      }
      return Promise.resolve(modifiedData);
    } catch (error) {
      console.error('Error fetching locations:', error);
      throw error;
    }
  }

  /**
   * Fetch tasks from the API
   * @param {string} sortType - Sort type for tasks (default: 'asc')
   * @returns {Promise<Array>} Promise that resolves to array of tasks
   */
  async fetchTasks({ filters = {}, page = 0, sort }) {
    try {
      // Create URL search params for GET request
      const params = new URLSearchParams({
        type: 'created',
        page_size: 20,
        // version: '2',
      });

      if (!!size(sort)) {
        Object.entries(sort).forEach(([key, value]) => {
          params.append('sortParam', key);
          params.append('sortType', value);
        });
      }

      // Handle filters - support both single values and arrays for multiple selection
      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          // Multiple selection - join with comma and encode the string
          const joinedValue = value.join(',');
          params.append(key, joinedValue);
        } else if (value !== undefined && value !== null && value !== '') {
          // Single value
          params.append(key, value);
        }
      });

      params.append('page', page > 0 ? page : 1);

      const response = await fetch(
        `${apiEndpoint}/api/v2/get_tasks?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        // Throw an error to be caught by the catch block
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      return Promise.resolve(data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw error;
    }
  }

  /**
   * Delete a task (mark as cancelled)
   * @param {string|number} taskId - ID of the task to delete
   * @param {string|number} version - Task version
   * @param {string} taskTitle - Task title (optional, for tracking)
   * @returns {Promise<void>} Promise that resolves when task is deleted
   */
  async deleteTask(taskId, version, taskTitle = '') {
    try {
      const taskData = new FormData();
      taskData.append('version', version);
      taskData.append('taskId', taskId);
      taskData.append('action', 'status_update');
      taskData.append('status', 'cancelled');

      const response = await fetch(apiEndpoint + '/api/v2/modify_task/', {
        method: 'POST',
        credentials: 'include',
        headers: this.getAuthHeaders(),
        body: taskData,
      });

      if (!response.ok) {
        // Throw an error to be caught by the catch block
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Track task deletion in Heap
      trackTaskDeleted({
        taskId: taskId,
        taskTitle: taskTitle,
      });

      showToast({
        priority: 'success',
        content: 'Task cancelled successfully',
        autoClose: 6000,
      });
    } catch (error) {
      console.error('Error deleting task:', error);

      showToast({
        priority: 'error',
        content: 'Failed to cancel task: ' + (error.message || 'Unknown error'),
        autoClose: 6000,
      });

      throw error;
    }
  }

  async getTaskDetails(taskId, version) {
    if (!size(String(taskId))) return;
    try {
      const params = new URLSearchParams({
        version: version || '1',
      });
      const response = await fetch(
        `${apiEndpoint}/api/v1/task/${taskId}?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: this.getAuthHeaders(),
        }
      );
      if (!response.ok) {
        // Throw an error to be caught by the catch block
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      return Promise.resolve(data[0]);
    } catch (error) {
      console.error('Error fetching task details:', error);
      throw error;
    }
  }

  async getDashboardTasks() {
    try {
      const params = new URLSearchParams({
        type: 'assigned',
        status: 'todo,in-progress',
      });
      const taskPromise = fetch(
        `${apiEndpoint}/api/v2/get_tasks?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: this.getAuthHeaders(),
        }
      );
      const [users, taskResponse] = await Promise.all([
        this.fetchUsers(),
        taskPromise,
      ]);

      if (!taskResponse.ok) {
        throw new Error(`HTTP error! Status: ${taskResponse.status}`);
      }

      const tasks = await taskResponse.json();
      const mergedTasks = mergeUsersToTasks(tasks.data, users);
      return Promise.resolve(mergedTasks);
    } catch (err) {
      console.error('Error fetching dashboard tasks:', err);
    }
  }
}

// Export a singleton instance
export const taskManagerService = new TaskManagerService();
export default taskManagerService;
